import { Link } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import * as m from "~/paraglide/messages.js";
import {
  AgentTeamMock,
  FullSystemMock,
  MakeMoneyMock,
  ResearchMock,
  SaveTimeMock,
} from "./mocks";
import { ProofRow } from "./proof-row";

type CapabilityKey = "save" | "money" | "agents" | "research" | "full";

interface CapabilityPanel {
  body: string;
  key: CapabilityKey;
  label: string;
  /** A case study slug, or null for the workbench example on this page. */
  link: { label: string; slug: string | null };
  mock: React.ReactNode;
  title: string;
}

function getPanels(): CapabilityPanel[] {
  return [
    {
      body: m.cap_save_body(),
      key: "save",
      label: m.cap_save_label(),
      link: { label: m.cap_save_link(), slug: null },
      mock: <SaveTimeMock />,
      title: m.cap_save_title(),
    },
    {
      body: m.cap_money_body(),
      key: "money",
      label: m.cap_money_label(),
      link: { label: m.cap_money_link(), slug: "lead-radar" },
      mock: <MakeMoneyMock />,
      title: m.cap_money_title(),
    },
    {
      body: m.cap_agents_body(),
      key: "agents",
      label: m.cap_agents_label(),
      link: { label: m.cap_agents_link(), slug: "ai-agent-team" },
      mock: <AgentTeamMock />,
      title: m.cap_agents_title(),
    },
    {
      body: m.cap_research_body(),
      key: "research",
      label: m.cap_research_label(),
      link: { label: m.cap_research_link(), slug: "blog-engine" },
      mock: <ResearchMock />,
      title: m.cap_research_title(),
    },
    {
      body: m.cap_full_body(),
      key: "full",
      label: m.cap_full_label(),
      link: { label: m.cap_full_link(), slug: "ai-infrastructure" },
      mock: <FullSystemMock />,
      title: m.cap_full_title(),
    },
  ];
}

/**
 * Attio's sticky-index section: the five kinds of system Lindevo builds, a
 * list on the left that follows the scroll, and one panel per kind on the
 * right with a product fragment and a link to the system it is drawn from.
 *
 * The list is plain anchors, so it works without JavaScript; the observer only
 * moves `aria-current`. Every panel is always in the document.
 */
export function Capabilities() {
  const titleId = useId();
  const panels = getPanels();
  const [active, setActive] = useState<CapabilityKey>("save");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id.replace("cap-", "") as CapabilityKey);
          }
        }
      },
      // A thin band across the upper middle of the viewport: the panel crossing
      // it is the one being read.
      { rootMargin: "-35% 0px -60% 0px" }
    );
    for (const el of document.querySelectorAll(".cap-panel")) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section
      aria-labelledby={titleId}
      className="capabilities wrap"
      id="capabilities"
    >
      <div className="section-heading">
        <h2 id={titleId}>{m.cap_title()}</h2>
        <p>{m.cap_lede()}</p>
      </div>

      <div className="cap-layout">
        <nav aria-label={m.cap_nav_label()} className="cap-nav">
          <ul>
            {panels.map((panel) => (
              <li key={panel.key}>
                <a
                  aria-current={active === panel.key ? "true" : undefined}
                  href={`#cap-${panel.key}`}
                >
                  {panel.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="cap-panels">
          {panels.map((panel) => (
            <article
              aria-labelledby={`cap-${panel.key}-title`}
              className="cap-panel"
              id={`cap-${panel.key}`}
              key={panel.key}
            >
              <div className="cap-copy">
                <p className="cap-label">{panel.label}</p>
                <h3 id={`cap-${panel.key}-title`}>{panel.title}</h3>
                <p>{panel.body}</p>
                {panel.link.slug ? (
                  <Link
                    className="quiet-link"
                    params={{ projectId: panel.link.slug }}
                    to="/projects/$projectId"
                  >
                    {panel.link.label} <span aria-hidden="true">↗</span>
                  </Link>
                ) : (
                  <a className="quiet-link" href="#work">
                    {panel.link.label} <span aria-hidden="true">↑</span>
                  </a>
                )}
              </div>
              {panel.mock}
            </article>
          ))}
        </div>
      </div>

      <div className="cap-promises">
        <h3>{m.cap_promise_title()}</h3>
        <dl>
          <div>
            <dt>{m.cap_promise_1()}</dt>
            <dd>{m.cap_promise_1_detail()}</dd>
          </div>
          <div>
            <dt>{m.cap_promise_2()}</dt>
            <dd>{m.cap_promise_2_detail()}</dd>
          </div>
          <div>
            <dt>{m.cap_promise_3()}</dt>
            <dd>{m.cap_promise_3_detail()}</dd>
          </div>
          <div>
            <dt>{m.cap_promise_4()}</dt>
            <dd>{m.cap_promise_4_detail()}</dd>
          </div>
        </dl>
      </div>

      <ProofRow />
    </section>
  );
}
